import "./App.css";
import { useEffect } from "react";
import { BrowserRouter, Routes, Route, Navigate, useParams, useLocation } from "react-router-dom";
import { Toaster } from "sonner";
import { categoryFromParam } from "./lib/categories";
import { setMeta } from "./lib/usePageMeta";
import { AuthProvider, useAuth } from "./context/AuthContext";
import Nav from "./components/Nav";
import Footer from "./components/Footer";
import CookieNotice from "./components/CookieNotice";
import AnalyticsTracker from "./components/AnalyticsTracker";
import Home from "./pages/Home";
import BlogList from "./pages/BlogList";
import PostDetail from "./pages/PostDetail";
import About from "./pages/About";
import Privacy from "./pages/Privacy";
import Terms from "./pages/Terms";
import Glossary from "./pages/Glossary";
import GlossaryTerm from "./pages/GlossaryTerm";
import AdminLogin from "./pages/AdminLogin";
import AdminDashboard from "./pages/AdminDashboard";
import AdminSubscribers from "./pages/AdminSubscribers";
import AdminComments from "./pages/AdminComments";
import AdminDrafts from "./pages/AdminDrafts";
import AdminFactChecks from "./pages/AdminFactChecks";
import PostEditor from "./pages/PostEditor";

function ProtectedRoute({ children }) {
  const { user, loading } = useAuth();
  if (loading || user === null) {
    return (
      <div className="max-w-3xl mx-auto px-6 py-32 text-center">
        <p className="font-mono text-[11px] tracking-widest uppercase text-[#66726d] animate-pulse">
          checking…
        </p>
      </div>
    );
  }
  if (!user) return <Navigate to="/admin/login" replace />;
  return children;
}

function CategoryRoute() {
  const { name } = useParams();
  const cat = categoryFromParam(name);
  if (!cat) return <Navigate to="/blog" replace />;
  // Legacy "/category/Origins %26 History" URLs → canonical slug form.
  if (decodeURIComponent(name) !== cat.slug) return <Navigate to={`/category/${cat.slug}`} replace />;
  return <BlogList forcedCategory={cat.name} />;
}

function AdminNoindex() {
  const { pathname } = useLocation();
  useEffect(() => {
    if (!pathname.startsWith("/admin")) return;
    setMeta('meta[name="robots"]', "noindex, nofollow");
    return () => setMeta('meta[name="robots"]', "index, follow, max-image-preview:large");
  }, [pathname]);
  return null;
}

export default function App() {
  return (
    <AuthProvider>
      <BrowserRouter>
        <div className="App">
          <AnalyticsTracker />
          <AdminNoindex />
          <Nav />
          <main>
            <Routes>
              <Route path="/" element={<Home />} />
              <Route path="/blog" element={<BlogList />} />
              <Route path="/blog/:slug" element={<PostDetail />} />
              <Route path="/category/:name" element={<CategoryRoute />} />
              <Route path="/glossary" element={<Glossary />} />
              <Route path="/glossary/:slug" element={<GlossaryTerm />} />
              <Route path="/about" element={<About />} />
              <Route path="/privacy" element={<Privacy />} />
              <Route path="/terms" element={<Terms />} />
              <Route path="/admin/login" element={<AdminLogin />} />
              <Route
                path="/admin"
                element={
                  <ProtectedRoute>
                    <AdminDashboard />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/admin/posts/new"
                element={
                  <ProtectedRoute>
                    <PostEditor />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/admin/posts/:id/edit"
                element={
                  <ProtectedRoute>
                    <PostEditor />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/admin/subscribers"
                element={
                  <ProtectedRoute>
                    <AdminSubscribers />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/admin/comments"
                element={
                  <ProtectedRoute>
                    <AdminComments />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/admin/drafts"
                element={
                  <ProtectedRoute>
                    <AdminDrafts />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/admin/fact-checks"
                element={
                  <ProtectedRoute>
                    <AdminFactChecks />
                  </ProtectedRoute>
                }
              />
              <Route path="*" element={<Navigate to="/" replace />} />
            </Routes>
          </main>
          <Footer />
          <Toaster position="bottom-right" richColors />
          <CookieNotice />
        </div>
      </BrowserRouter>
    </AuthProvider>
  );
}
